/**
 * Trunnion AI - Mission Board Component
 * Main Kanban-style board for organizing workflows into mission columns.
 * Provides a horizontally scrollable layout of mission columns with
 * drag-and-drop support for moving workflows between missions.
 */

import { useState, useCallback } from 'react';
import { Plus, Target } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useDragAndDrop, type DragItem } from '@/hooks/useDragAndDrop';
import { MissionColumn, type MissionColor, type MissionWorkflow } from './MissionColumn';

/** A mission containing a set of workflows displayed as a Kanban column. */
interface Mission {
  /** Unique mission identifier */
  id: string;
  /** Mission display name */
  name: string;
  /** Color theme for the column header */
  color: MissionColor;
  /** Workflows assigned to this mission */
  workflows: MissionWorkflow[];
}

/**
 * Mock data representing the default set of missions and their workflows.
 * Uses realistic defense operation names and classification levels.
 */
const INITIAL_MISSIONS: Mission[] = [
  {
    id: 'active-operations',
    name: 'Active Operations',
    color: 'gold',
    workflows: [
      {
        id: 'wf-001',
        name: 'SIGINT Collection Pipeline',
        classification: 'TS_SCI',
        executionStatus: 'running',
        agentCount: 5,
      },
      {
        id: 'wf-002',
        name: 'Force Protection Monitoring',
        classification: 'SECRET',
        executionStatus: 'success',
        agentCount: 3,
      },
      {
        id: 'wf-003',
        name: 'Threat Assessment Aggregator',
        classification: 'SECRET',
        executionStatus: 'running',
        agentCount: 4,
      },
    ],
  },
  {
    id: 'planning',
    name: 'Planning',
    color: 'blue',
    workflows: [
      {
        id: 'wf-004',
        name: 'ISR Tasking Automation',
        classification: 'TOP_SECRET',
        executionStatus: 'pending',
        agentCount: 2,
      },
      {
        id: 'wf-005',
        name: 'Logistics Route Optimizer',
        classification: 'CUI',
        executionStatus: 'never_executed',
        agentCount: 3,
      },
      {
        id: 'wf-006',
        name: 'Joint Fires Deconfliction',
        classification: 'SECRET',
        executionStatus: 'failed',
        agentCount: 6,
      },
      {
        id: 'wf-007',
        name: 'CBRN Sensor Fusion',
        classification: 'TOP_SECRET',
        executionStatus: 'never_executed',
        agentCount: 4,
      },
    ],
  },
  {
    id: 'complete',
    name: 'Complete',
    color: 'green',
    workflows: [
      {
        id: 'wf-008',
        name: 'Battle Damage Assessment',
        classification: 'SECRET',
        executionStatus: 'success',
        agentCount: 3,
      },
      {
        id: 'wf-009',
        name: 'MEDEVAC Coordination',
        classification: 'UNCLASSIFIED',
        executionStatus: 'success',
        agentCount: 2,
      },
    ],
  },
  {
    id: 'archive',
    name: 'Archive',
    color: 'slate',
    workflows: [
      {
        id: 'wf-010',
        name: 'Exercise NORTHERN EDGE AAR',
        classification: 'CUI',
        executionStatus: 'success',
        agentCount: 1,
      },
    ],
  },
];

/**
 * Main Kanban board for the missions feature.
 *
 * Renders a header with an "Add Mission" action and a horizontally scrollable
 * row of MissionColumn components. Workflows can be dragged between columns
 * using HTML5 drag-and-drop, powered by the useDragAndDrop hook.
 *
 * @example
 * ```tsx
 * <MissionBoard />
 * ```
 */
export function MissionBoard() {
  const [missions, setMissions] = useState<Mission[]>(INITIAL_MISSIONS);

  /**
   * Moves a workflow from one mission column to another.
   * Removes the workflow from the source mission and appends it to the target.
   */
  const moveWorkflow = useCallback(
    (item: DragItem, targetColumn: string) => {
      setMissions((prev) => {
        const sourceMission = prev.find((m) => m.id === item.sourceColumn);
        if (!sourceMission) return prev;

        const workflow = sourceMission.workflows.find((w) => w.id === item.id);
        if (!workflow) return prev;

        return prev.map((mission) => {
          if (mission.id === item.sourceColumn) {
            return {
              ...mission,
              workflows: mission.workflows.filter((w) => w.id !== item.id),
            };
          }
          if (mission.id === targetColumn) {
            return {
              ...mission,
              workflows: [...mission.workflows, workflow],
            };
          }
          return mission;
        });
      });
    },
    [],
  );

  const {
    draggedItem,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop({ onDrop: moveWorkflow });

  /** Placeholder for adding a new mission column. */
  const handleAddMission = () => {
    // Future: open a modal or inline form to create a new mission
  };

  /** Placeholder for adding a workflow to a specific mission. */
  const handleAddWorkflow = (_missionId: string) => {
    // Future: open a modal or inline form to assign a workflow
  };

  return (
    <div className="flex h-full flex-col">
      {/* Board header */}
      <div className="flex items-center justify-between border-b border-trunnion-slate-700 px-6 py-4">
        <div className="flex items-center gap-3">
          <Target className="h-5 w-5 text-trunnion-gold-500" />
          <div>
            <h1 className="text-lg font-semibold text-white">Mission Board</h1>
            <p className="text-xs text-trunnion-slate-400">
              Organize workflows by mission objective
            </p>
          </div>
        </div>

        <Button
          variant="primary"
          size="sm"
          icon={<Plus className="h-4 w-4" />}
          onClick={handleAddMission}
        >
          Add Mission
        </Button>
      </div>

      {/* Horizontally scrollable column container */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-4 p-6 h-full min-w-max">
          {missions.map((mission) => (
            <MissionColumn
              key={mission.id}
              id={mission.id}
              name={mission.name}
              color={mission.color}
              workflows={mission.workflows}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              draggedItemId={draggedItem?.id ?? null}
              onAddWorkflow={handleAddWorkflow}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
